import React, { useState, useEffect, useMemo, useRef } from "react";
import DataTable from "react-data-table-component";
import Modal from "react-modal";
import { Search, ChevronDown, ChevronLeft, ChevronRight, Download, Upload, BarChart3, Edit, Filter, RotateCcw, X } from "lucide-react";
import historyIcon from "../../icons/inventory/dashboard/history.png";
import editIcon from "../../icons/inventory/dashboard/edit.png";
import ImportProductsModal from "../../components/ImportProductsModal";
import LoadingModal from "../../components/LoadingModal";
import { logExport } from "../../services/ExportLogService";
import ItemApproval from "./ItemApproval";
import CategoryClassificationScreen from "./CategoryClassificationScreen";
import MarkupSettingModal from "../../components/MarkupSettingModal";
import UomManager from "../../components/UomManager";
import EditProductModal from "../../components/EditProductModal";
import { adminServices } from "../../services/AdminServices";
import { toast } from "react-toastify";
import axios from "axios";
const paginationStyles = {
  wrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    padding: "16px 0",
    gap: "8px",
    flexWrap: "wrap"
  },
  link: {
    color: "#6b7280",
    fontSize: "0.875rem",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: "none",
    border: "none",
    padding: "6px 8px"
  },
  linkDisabled: {
    color: "#9ca3af",
    cursor: "not-allowed",
    opacity: 0.7
  },
  pageNum: {
    color: "#6b7280",
    fontSize: "0.875rem",
    cursor: "pointer",
    minWidth: "32px",
    height: "32px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 8px",
    border: "none",
    backgroundColor: "transparent",
    borderRadius: "6px"
  },
  pageNumActive: {
    backgroundColor: "#e5e7eb",
    color: "#374151",
    fontWeight: 500
  },
  ellipsis: {
    color: "#6b7280",
    fontSize: "0.875rem",
    padding: "0 4px",
    minWidth: "24px",
    textAlign: "center"
  }
};
const itemTypes = [{
  label: "Regular Items",
  value: "REGULAR"
}, {
  label: "Regulated Items",
  value: "REGULATED"
}, {
  label: "Special Items",
  value: "SPECIAL"
}];
function CustomPagination({
  rowCount,
  rowsPerPage,
  currentPage,
  onChangePage,
  totalRows
}) {
  const total = rowCount ?? totalRows ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;
  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({
        length: totalPages
      }, (_, i) => i + 1);
    }
    const pages = [];
    if (currentPage <= 4) {
      for (let i = 1; i <= Math.min(5, totalPages); i++) pages.push(i);
      if (totalPages > 5) pages.push("...");
      if (totalPages > 5) pages.push(totalPages);
    } else if (currentPage >= totalPages - 3) {
      pages.push(1);
      if (totalPages > 5) pages.push("...");
      for (let i = Math.max(1, totalPages - 4); i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      pages.push("...");
      for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
      pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };
  const pageNumbers = getPageNumbers();
  return <div style={paginationStyles.wrapper}>
      <button type="button" aria-label="Previous page" style={{
      ...paginationStyles.link,
      ...(!canPrev ? paginationStyles.linkDisabled : {})
    }} onClick={() => canPrev && onChangePage(currentPage - 1)} disabled={!canPrev}>
        <ChevronLeft size={18} />
        Previous
      </button>
      {pageNumbers.map((p, i) => p === "..." ? <span key={`ellipsis-${i}`} style={paginationStyles.ellipsis}>
            ...
          </span> : <button key={p} type="button" style={{
      ...paginationStyles.pageNum,
      ...(p === currentPage ? paginationStyles.pageNumActive : {})
    }} onClick={() => onChangePage(p)}>
            {p}
          </button>)}
      <button type="button" aria-label="Next page" style={{
      ...paginationStyles.link,
      ...(!canNext ? paginationStyles.linkDisabled : {})
    }} onClick={() => canNext && onChangePage(currentPage + 1)} disabled={!canNext}>
        Next
        <ChevronRight size={18} />
      </button>
    </div>;
}
const ItemMaster = () => {
  const [itemType, setItemType] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("Category");
  const [activitySearchQuery, setActivitySearchQuery] = useState("");
  const [activityFilter, setActivityFilter] = useState("All Activities");
  const [activityDropdownOpen, setActivityDropdownOpen] = useState(false);
  const activityDropdownRef = useRef(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [showItemApproval, setShowItemApproval] = useState(false);
  const [isMarkupModalOpen, setIsMarkupModalOpen] = useState(false);
  const [isUomManagerOpen, setIsUomManagerOpen] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateProducts, setDuplicateProducts] = useState([]);
  const [pendingImportData, setPendingImportData] = useState(null);
  const [itemMasterData, setItemMasterData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [subclassifications, setSubclassifications] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [isEditProductModalOpen, setIsEditProductModalOpen] = useState(false);
  const [editProductLoading, setEditProductLoading] = useState(false);
  const [showCategoryScreen, setShowCategoryScreen] = useState(false);
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [addProductForm, setAddProductForm] = useState({
    sku: "",
    barcode: "",
    type: "",
    name: "",
    details: "",
    status: "",
    regulatedLimit: "",
    brand: "",
    variant: "",
    bucorRemarks: "",
    subclassficationId: "",
    productClassificationId: "",
    productCategoryId: ""
  });
  const [showItemTypesDropdown, setShowItemTypesDropdown] = useState(false);
  const itemTypesDropdownRef = useRef(null);
  const [addProductLoading, setAddProductLoading] = useState(false);
  const [itemApprovalData, setItemApprovalData] = useState([]);
  const [itemApprovalLoading, setItemApprovalLoading] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [historyProduct, setHistoryProduct] = useState(null);
  const [historyFrom, setHistoryFrom] = useState("");
  const [historyTo, setHistoryTo] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalRecords, setHistoryTotalRecords] = useState(0);
  const HISTORY_LIMIT = 10;
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotalRecords, setActivityTotalRecords] = useState(0);
  const ACTIVITY_LIMIT = 10;
  const fetchProductHistory = async ({
    productId,
    dateFrom,
    dateTo,
    page = 1,
    limit = HISTORY_LIMIT
  }) => {
    if (!productId) return;
    try {
      setHistoryLoading(true);
      const res = await adminServices.getProductHistory({
        productId,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        page,
        limit
      });
      const history = res?.data?.history ?? res?.data?.data ?? res?.data;
      const raw = Array.isArray(history) ? history : Array.isArray(res?.data) ? res.data : [];
      setHistoryTotalRecords(res?.data?.totalRecords ?? res?.data?.total ?? raw.length);
      const mapped = raw.map((entry, index) => {
        const changedBy = entry.userName || entry.changedBy || entry.updatedBy || entry.user || "-";
        const dtRaw = entry.dateTime || entry.date || entry.createdAt || entry.updatedAt || "";
        const dt = dtRaw ? new Date(dtRaw) : null;
        const date = dt && !Number.isNaN(dt.getTime()) ? dt.toLocaleString() : dtRaw || "-";
        const table = entry.table || entry.tableName || "-";
        return {
          id: entry.id ?? entry.auditLogId ?? index + 1,
          tableNo: (page - 1) * limit + index + 1,
          table,
          changedBy,
          date
        };
      });
      setHistoryRows(mapped);
    } catch (error) {
      console.error("Error fetching product history:", error);
      setHistoryRows([]);
      setHistoryTotalRecords(0);
      toast.error(error?.response?.data?.message || "Failed to load product history.");
    } finally {
      setHistoryLoading(false);
    }
  };
  const openHistoryModal = row => {
    const productId = row?.id ?? row?.originalData?.id;
    if (!productId) {
      toast.error("Missing product id.");
      return;
    }
    setHistoryProduct({
      id: productId,
      sku: row?.sku,
      description: row?.description
    });
    setHistoryFrom("");
    setHistoryTo("");
    setHistoryRows([]);
    setHistoryPage(1);
    setIsHistoryModalOpen(true);
    fetchProductHistory({
      productId,
      page: 1,
      limit: HISTORY_LIMIT
    });
  };

  // Download Template handler
  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BASE_URL || ''}import/download-template`, {
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('appToken')
        },
        responseType: 'blob'
      });
      // Handle file download
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'item-master-template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading template:", error);
      alert("Failed to download template. Please try again.");
    }
  };

  // Import handler
  const handleImport = async data => {
    try {
      const formData = new FormData();
      formData.append('file', data.file);
      if (data.forBuCorApproval) {
        formData.append('forBuCorApproval', 'true');
        formData.append('reasonForListing', data.reasonForListing);
      }

      // Validate first
      const validateResponse = await axios.post(`${process.env.REACT_APP_BASE_URL || ''}import/validate`, formData, {
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('appToken')
        }
      });

      // Check for duplicates
      if (validateResponse.data && validateResponse.data.hasDuplicates) {
        // Show duplicate modal
        setDuplicateProducts(validateResponse.data.duplicatedProducts || []);
        setPendingImportData(data);
        setShowDuplicateModal(true);
        setIsImportModalOpen(false);
        return;
      }

      // If no duplicates, proceed with import
      await proceedWithImport(data, formData);
    } catch (error) {
      console.error("Error importing products:", error);
      alert(error.response?.data?.message || "Failed to import products. Please try again.");
    }
  };

  // Proceed with import after validation
  const proceedWithImport = async (data, formData) => {
    try {
      const withApproval = data.forBuCorApproval ? 'true' : 'false';
      const importResponse = await axios.post(`${process.env.REACT_APP_BASE_URL || ''}import?withApproval=${withApproval}`, formData, {
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('appToken')
        }
      });

      //console.log("Import successful:", importResponse);

      // Handle response structure
      if (importResponse.data && importResponse.data.success) {
        const {
          message,
          totalRecords,
          successfulRecords,
          failedRecords,
          bucorRequestNo
        } = importResponse.data;
        let successMessage = message || "Products imported successfully!";
        if (totalRecords !== undefined) {
          successMessage += ` (${successfulRecords}/${totalRecords} records)`;
          if (failedRecords > 0) {
            successMessage += `, ${failedRecords} failed`;
          }
        }
        if (bucorRequestNo) {
          successMessage += ` - BuCor Request: ${bucorRequestNo}`;
        }
        toast.success(successMessage);
      } else {
        toast.success("Products imported successfully!");
      }
      setIsImportModalOpen(false);
      setShowDuplicateModal(false);
      setPendingImportData(null);
      // Refresh the products list after successful import
      fetchProducts();
    } catch (error) {
      console.error("Error importing products:", error);
      alert(error.response?.data?.message || "Failed to import products. Please try again.");
    }
  };

  // Handle proceed with duplicates
  const handleProceedWithDuplicates = async () => {
    if (pendingImportData) {
      const formData = new FormData();
      formData.append('file', pendingImportData.file);
      if (pendingImportData.forBuCorApproval) {
        formData.append('forBuCorApproval', 'true');
        formData.append('reasonForListing', pendingImportData.reasonForListing);
      }
      await proceedWithImport(pendingImportData, formData);
    }
  };
  const fetchClassifications = async () => {
    try {
      const res = await adminServices.storeProcedure2("classifications", {});
      const data = res?.data;
      setClassifications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching classifications:", err);
      setClassifications([]);
    }
  };
  const fetchSubclassifications = async () => {
    try {
      const res = await adminServices.storeProcedure2("subclassifications", {});
      const data = res?.data;
      setSubclassifications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching subclassifications:", err);
      setSubclassifications([]);
    }
  };

  // Fetch products, categories from API
  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchClassifications();
    fetchSubclassifications();
  }, []);

  // Load products when item type changes
  useEffect(() => {
    fetchProducts(null, null, itemType);
  }, [itemType]);

  // Activity log: fetch when page changes (page/limit params)
  useEffect(() => {
    fetchActivityLog(activityPage, ACTIVITY_LIMIT);
  }, [activityPage]);

  // Reset activity log to page 1 when search or filter changes
  useEffect(() => {
    setActivityPage(1);
  }, [activitySearchQuery, activityFilter]);

  // Close activity dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = e => {
      if (activityDropdownRef.current && !activityDropdownRef.current.contains(e.target)) {
        setActivityDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
    if (!showItemApproval) return;
    const fetchItemApprovals = async () => {
      try {
        setItemApprovalLoading(true);
        const res = await adminServices.getNewItemApprovals();
        const raw = Array.isArray(res?.data) ? res.data : Array.isArray(res?.data?.items) ? res.data.items : [];
        const mapped = raw.map((item, index) => {
          const merchantName = item.requestedByMerchantNavigation?.merchantName ?? item.requestedBy ?? item.requestor ?? "-";
          const itemsCount = Array.isArray(item.merchantItemRequestItems) ? item.merchantItemRequestItems.length : item.numberOfItems ?? item.itemsCount ?? 0;
          const rawStatus = String(item.status || "").toUpperCase();
          const status = rawStatus === "APPROVED" ? "Approved" : rawStatus === "REJECTED" ? "Rejected" : rawStatus === "PENDING BUCOR APPROVAL" ? "Pending Bucor Approval" : rawStatus === "NEW" || rawStatus === "PENDING" ? "Pending" : item.status || "Pending";
          return {
            id: item.id ?? item.requestId ?? index + 1,
            requestNumber: item.requestNo ?? item.requestNumber ?? "-",
            requestedBy: merchantName,
            requestedByMerchant: item.requestedByMerchant ?? item.requestedByMerchantNavigation?.id,
            requestedByMerchantNavigation: item.requestedByMerchantNavigation,
            numberOfItems: itemsCount,
            status,
            remarks: item.reasonForListing ?? item.remarks ?? item.comment ?? "-",
            bucorRemarks: item.bucorRemarks ?? item.remarks ?? "-",
            originalData: item
          };
        });
        setItemApprovalData(mapped);
      } catch (error) {
        console.error("Error fetching new item approvals:", error);
        setItemApprovalData([]);
      } finally {
        setItemApprovalLoading(false);
      }
    };
    fetchItemApprovals();
  }, [showItemApproval]);
  const fetchActivityLog = async (page = 1, limit = ACTIVITY_LIMIT) => {
    try {
      setActivityLoading(true);
      const res = await adminServices.getProductHistory({
        page,
        limit
      });
      const history = res?.data?.history ?? res?.data?.data ?? res?.data;
      const raw = Array.isArray(history) ? history : Array.isArray(res?.data) ? res.data : [];
      setActivityTotalRecords(res?.data?.totalRecords ?? res?.data?.total ?? raw.length);
      const mapped = raw.map((entry, index) => {
        const name = entry.userName || "";
        const email = entry.email || "";
        const firstLetter = name ? name.charAt(0).toUpperCase() : email ? email.charAt(0).toUpperCase() : "?";
        const avatarColor = "#0EA5E9";
        const ts = entry.dateTime ? new Date(entry.dateTime) : null;
        const timestamp = ts && !Number.isNaN(ts.getTime()) ? ts.toLocaleString() : "";
        const activityRaw = entry.action || "";
        const activity = activityRaw.replace(/_/g, " ") || "";
        const details = entry.description.replace(/_/g, " ") || "";
        return {
          id: entry.auditLogId ?? (page - 1) * limit + index + 1,
          timestamp,
          dateObj: ts,
          activity,
          user: name || email ? {
            name,
            email,
            avatar: firstLetter,
            avatarColor
          } : null,
          details
        };
      });
      // Sort by date descending (newest first); null dates go last
      mapped.sort((a, b) => {
        const ta = a.dateObj ? a.dateObj.getTime() : 0;
        const tb = b.dateObj ? b.dateObj.getTime() : 0;
        return tb - ta;
      });
      setActivityLog(mapped);
    } catch (error) {
      console.error("Error fetching product activity history:", error);
      setActivityLog([]);
      setActivityTotalRecords(0);
    } finally {
      setActivityLoading(false);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (!event.target.closest('.item-master-filter-btn') && !event.target.closest('[style*="position: absolute"]')) {
        setShowCategoryDropdown(false);
      }
    };
    if (showCategoryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showCategoryDropdown]);

  // Close item types dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (itemTypesDropdownRef.current && !itemTypesDropdownRef.current.contains(event.target)) {
        setShowItemTypesDropdown(false);
      }
    };
    if (showItemTypesDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showItemTypesDropdown]);

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      const response = await adminServices.getData('ProductCategories');
      if (response && response.data) {
        const categoriesList = Array.isArray(response.data) ? response.data.map(cat => ({
          id: cat.id,
          name: cat.category,
          description: cat.description
        })) : [];
        setCategories(categoriesList);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    }
  };
  const fetchProducts = async (facility = null, merchantId = null, itemTypeFilter = null) => {
    setLoading(true);
    try {
      // Build query string with optional parameters
      let endpoint = 'products/data';
      const params = [];
      if (facility) {
        params.push(`facility=${encodeURIComponent(facility)}`);
      }
      if (merchantId) {
        params.push(`merchantId=${encodeURIComponent(merchantId)}`);
      }
      if (itemTypeFilter) {
        params.push(`type=${encodeURIComponent(itemTypeFilter)}`);
      }
      if (params.length > 0) {
        endpoint += '?' + params.join('&');
      }
      const response = await adminServices.getData(endpoint);
      if (response && response.data) {
        // Map API response to table format
        const mappedData = Array.isArray(response.data) ? response.data.map((product, index) => {
          return {
            id: product.id || index + 1,
            sku: product.sku || "-",
            barcode: product.barcode || "-",
            description: product.description || "-",
            category: product.category || "-",
            classification: product.classification || "-",
            subCategory: product.subCategory || "-",
            brand: product.brand || "-",
            variant: product.variant || "-",
            largestUOM: product.largestUOM || "-",
            smallestUOM: product.smallestUOM || "-",
            marketPrice: product.marketPrice !== undefined && product.marketPrice !== null ? `P${product.marketPrice.toLocaleString()}` : "-",
            profitMargin: product.profitMargin !== undefined && product.profitMargin !== null ? product.profitMargin.toString() : "-",
            isActive: product.status == 'ACTIVE' || product.status == 'NEW',
            // Default to active, can be updated based on status if available
            status: product.status || "-",
            settings: product.settings || {},
            type: product.type || "REGULAR",
            originalData: product
          };
        }) : [];
        var filteredData = itemType ? mappedData.filter(item => item.type === itemType) : mappedData;
        setItemMasterData(filteredData);
        toast.success("Items loaded successfully");
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      // Keep empty array on error
      setItemMasterData([]);
    } finally {
      setLoading(false);
    }
  };

  // Export to CSV function
  const exportToCSV = async data => {
    if (!data || data.length === 0) {
      alert("No data to export");
      return;
    }

    // Log the export
    await logExport('Item Master');
    const headers = ['SKU', 'Barcode', 'Description', 'Category', 'Classification', 'Sub-category', 'Brand', 'Variant', 'Largest UOM', 'Smallest UOM', 'Market Price', 'Profit Margin'];
    const rows = data.map(item => [item.sku || '', item.barcode || '', item.description || '', item.category || '', item.classification || '', item.subCategory || '', item.brand || '', item.variant || '', item.largestUOM || '', item.smallestUOM || '', item.marketPrice || '', item.profitMargin || '']);
    const csvContent = [headers, ...rows].map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'ItemMaster.csv');
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const handleEditProduct = async row => {
    const productId = row?.id ?? row?.originalData?.id;
    if (!productId) {
      toast.error("Product ID not found.");
      return;
    }
    setEditProductLoading(true);
    try {
      const response = await adminServices.getData(`Products/${productId}`);
      const data = response?.data;
      if (!data) {
        toast.error("Failed to load product details.");
        return;
      }
      const product = Array.isArray(data) ? data[0] : data;
      setEditProduct({
        ...row,
        originalData: product,
        id: product.id ?? productId,
        sku: product.sku ?? row.sku,
        barcode: product.barcode ?? row.barcode,
        description: product.description ?? product.name ?? row.description,
        category: product.category ?? product.productCategory?.category ?? row.category,
        classification: product.classification ?? product.productClassification?.name ?? row.classification,
        subCategory: product.subCategory ?? product.productSubclassification?.name ?? row.subCategory,
        brand: product.brand ?? row.brand,
        variant: product.variant ?? row.variant,
        largestUOM: product.largestUOM ?? product.largest_uom ?? row.largestUOM,
        smallestUOM: product.smallestUOM ?? product.smallest_uom ?? row.smallestUOM,
        marketPrice: product.marketPrice ?? row.marketPrice,
        profitMargin: product.profitMargin ?? row.profitMargin
      });
      setIsEditProductModalOpen(true);
    } catch (error) {
      console.error("Error fetching product:", error);
      toast.error(error.response?.data?.message || "Failed to load product details.");
    } finally {
      setEditProductLoading(false);
    }
  };
  const handleSaveProduct = async form => {
    try {
      const classificationName = classifications?.find(c => c.id === Number(form.productClassificationId))?.name ?? "";
      const subClassificationName = subclassifications?.find(sc => sc.id === Number(form.productSubclassificationId))?.name ?? "";
      const payload = {
        productId: Number(form.productId) || 0,
        name: form.name ?? form.details ?? "",
        brand: form.brand ?? "",
        variant: form.variant ?? "",
        details: form.details ?? "",
        category: form.category ?? "",
        classification: classificationName,
        subClassification: subClassificationName,
        markup: Number(form.markup) || 0,
        marketPrice: Number(form.marketPrice) || 0,
        supplierToIpxUom: form.supplierToIpxUom ?? "",
        supplierToIpxPrice: Number(form.supplierToIpxPrice) || 0,
        ipxToBigStoreUom: form.ipxToBigStoreUom ?? "",
        ipxToBigStorePrice: Number(form.ipxToBigStorePrice) || 0,
        ipxToSmallStoreUom: form.ipxToSmallStoreUom ?? "",
        ipxToSmallStorePrice: Number(form.ipxToSmallStorePrice) || 0,
        ipxToPdlUom: form.ipxToPdlUom ?? "",
        ipxToPdlPrice: Number(form.ipxToPdlPrice) || 0,
        bigStoreToSmallStoreUom: form.bigStoreToSmallStoreUom ?? "",
        bigStoreToSmallStorePrice: Number(form.bigStoreToSmallStorePrice) || 0,
        bigStoreToPdlUom: form.bigStoreToPdlUom ?? "",
        bigStoreToPdlPrice: Number(form.bigStoreToPdlPrice) || 0,
        smallStoreToPdlUom: form.smallStoreToPdlUom ?? "",
        smallStoreToPdlPrice: Number(form.smallStoreToPdlPrice) || 0
      };
      await adminServices.updateProduct(payload);
      toast.success("Product updated successfully.");
      fetchProducts();
      setIsEditProductModalOpen(false);
      setEditProduct(null);
    } catch (error) {
      console.error("Error updating product:", error);
      toast.error(error.response?.data?.message || "Failed to update product.");
      throw error;
    }
  };
  const handleAddProductChange = (field, value) => {
    setAddProductForm(prev => {
      const next = {
        ...prev,
        [field]: value
      };
      if (field === "productCategoryId") {
        next.productClassificationId = "";
        next.subclassficationId = "";
      } else if (field === "productClassificationId") {
        next.subclassficationId = "";
      }
      return next;
    });
  };
  const handleAddProductSubmit = async () => {
    try {
      setAddProductLoading(true);
      const payload = {
        subclassficationId: Number(addProductForm.subclassficationId) || 0,
        type: addProductForm.type || "",
        regulatedLimit: Number(addProductForm.regulatedLimit) || 0,
        sku: addProductForm.sku || "",
        barcode: addProductForm.barcode || "",
        name: addProductForm.name || "",
        brand: addProductForm.brand || "",
        variant: addProductForm.variant || "",
        details: addProductForm.details || "",
        status: addProductForm.status || "",
        bucorRemarks: addProductForm.bucorRemarks || ""
      };
      await adminServices.createProduct(payload);
      toast.success("Product added successfully.");
      fetchProducts();
      setIsAddProductModalOpen(false);
      setAddProductForm({
        sku: "",
        barcode: "",
        type: "",
        name: "",
        details: "",
        status: "",
        regulatedLimit: "",
        brand: "",
        variant: "",
        bucorRemarks: "",
        subclassficationId: "",
        productClassificationId: "",
        productCategoryId: ""
      });
    } catch (error) {
      console.error("Error creating product:", error);
      toast.error(error.response?.data?.message || "Failed to add product.");
    } finally {
      setAddProductLoading(false);
    }
  };

  // Filter data based on search query and category
  const filteredItemMasterData = useMemo(() => {
    let filtered = itemMasterData;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(item => {
        const searchableFields = [item.sku, item.barcode, item.description, item.brand, item.variant, item.category, item.classification, item.subCategory, item.status];
        return searchableFields.some(field => field && field.toString().toLowerCase().includes(query));
      });
    }

    // Apply category filter
    if (categoryFilter !== "Category") {
      filtered = filtered.filter(item => {
        return item.category === categoryFilter;
      });
    }
    return filtered;
  }, [itemMasterData, searchQuery, categoryFilter]);

  // Activity Log rows, filtered by search + activity type
  const activityLogData = useMemo(() => {
    let rows = activityLog;
    const q = activitySearchQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter(row => {
        return (row.activity || "").toLowerCase().includes(q) || (row.details || "").toLowerCase().includes(q) || (row.user?.name || "").toLowerCase().includes(q) || (row.user?.email || "").toLowerCase().includes(q);
      });
    }
    if (activityFilter && activityFilter !== "All Activities") {
      const f = activityFilter.toLowerCase();
      rows = rows.filter(row => String(row.activity || "").toLowerCase().includes(f));
    }
    return rows;
  }, [activityLog, activitySearchQuery, activityFilter]);
  const itemMasterColumns = [{
    name: "",
    selector: () => "",
    cell: () => <input type="checkbox" />,
    width: "50px",
    sortable: false
  }, {
    name: "SKU",
    selector: row => row.sku,
    sortable: true,
    minWidth: "140px"
  }, {
    name: "Barcode",
    selector: row => row.barcode,
    sortable: true,
    minWidth: "180px"
  }, {
    name: "Description",
    selector: row => row.description,
    sortable: true,
    minWidth: "250px"
  }, {
    name: "Category",
    selector: row => row.category,
    sortable: true,
    minWidth: "180px"
  }, {
    name: "Classification",
    selector: row => row.classification,
    sortable: true,
    minWidth: "150px"
  }, {
    name: "Sub-category",
    selector: row => row.subCategory,
    sortable: true,
    minWidth: "150px"
  }, {
    name: "Brand",
    selector: row => row.brand,
    sortable: true,
    minWidth: "120px"
  }, {
    name: "Variant",
    selector: row => row.variant,
    sortable: true,
    minWidth: "100px"
  }, {
    name: "Largest UOM",
    selector: row => row.largestUOM,
    sortable: true,
    minWidth: "140px"
  }, {
    name: "Smallest UOM",
    selector: row => row.smallestUOM,
    sortable: true,
    minWidth: "140px"
  }, {
    name: "Market Price",
    selector: row => row.marketPrice,
    sortable: true,
    minWidth: "140px"
  }, {
    name: "Profit Margin",
    selector: row => row.profitMargin,
    sortable: true,
    minWidth: "150px"
  }, {
    name: "Actions",
    selector: () => "",
    cell: row => <div style={{
      display: "flex",
      alignItems: "center",
      gap: "12px"
    }}>
          <img src={historyIcon} alt="History" title="History" style={{
        width: 18,
        height: 18,
        cursor: "pointer"
      }} onClick={e => {
        e.stopPropagation();
        openHistoryModal(row);
      }} />
          <img src={editIcon} alt="Edit" title="Edit" style={{
        width: 18,
        height: 18,
        cursor: "pointer"
      }} onClick={e => {
        e.stopPropagation();
        handleEditProduct(row);
      }} />
          <label style={{
        position: "relative",
        display: "inline-block",
        width: "44px",
        height: "24px"
      }}>
            <input type="checkbox" checked={row.status === "ACTIVE" || row.status === "NEW"} onChange={async () => {
          if (row.status === "ACTIVE" || row.status === "NEW") {
            await adminServices.storeProcedure2('disableProduct', {
              productId: row.id
            });
          } else {
            await adminServices.storeProcedure2('enableProduct', {
              productId: row.id
            });
          }
          await fetchProducts();
        }} style={{
          opacity: 0,
          width: 0,
          height: 0
        }} />
            <span style={{
          position: "absolute",
          cursor: "pointer",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: row.status === "ACTIVE" || row.status === "NEW" ? "#10b981" : "#d1d5db",
          borderRadius: "24px",
          transition: "0.3s"
        }}>
              <span style={{
            position: "absolute",
            content: '""',
            height: "18px",
            width: "18px",
            left: row.status === "ACTIVE" || row.status === "NEW" ? "22px" : "3px",
            bottom: "3px",
            backgroundColor: "#ffffff",
            borderRadius: "50%",
            transition: "0.3s"
          }} />
            </span>
          </label>
        </div>,
    sortable: false,
    minWidth: "150px"
  }];
  const activityLogColumns = [{
    name: "Timestamp",
    selector: row => row.timestamp,
    sortable: true,
    width: "25%"
  }, {
    name: "Activity",
    selector: row => row.activity,
    sortable: true,
    width: "25%"
  }, {
    name: "User/Source",
    sortable: true,
    width: "25%",
    cell: row => {
      if (!row.user) return "-";
      return <div style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        overflow: "hidden"
      }}>
            <div style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          backgroundColor: row.user.avatarColor || "#0EA5E9",
          color: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.875rem",
          fontWeight: 500,
          flexShrink: 0
        }}>
              {row.user.avatar || (row.user.name || row.user.email || "?").charAt(0).toUpperCase()}
            </div>
            <div style={{
          overflow: "hidden"
        }}>
              <div style={{
            fontSize: "0.875rem",
            fontWeight: 500,
            color: "#111827",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden"
          }} title={row.user.name || row.user.email || "-"}>
                {row.user.name || row.user.email || "-"}
              </div>
              {row.user.email && <div style={{
            fontSize: "0.75rem",
            color: "#6b7280",
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden"
          }} title={row.user.email}>
                  {row.user.email}
                </div>}
            </div>
          </div>;
    }
  }, {
    name: "Details",
    selector: row => row.details ?? "-",
    sortable: true,
    width: "25%",
    wrap: true
  }];
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#002347",
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: "600"
      }
    },
    headCells: {
      style: {
        color: "#ffffff",
        fontSize: "0.875rem",
        fontWeight: "600",
        whiteSpace: "normal",
        overflow: "visible",
        textOverflow: "clip",
        wordWrap: "break-word"
      }
    },
    cells: {
      style: {
        fontSize: "0.875rem",
        color: "#111827"
      }
    },
    pagination: {
      style: {
        justifyContent: "center",
        flexWrap: "wrap"
      }
    }
  };
  if (showItemApproval) {
    return <ItemApproval onBack={() => setShowItemApproval(false)} data={itemApprovalData} loading={itemApprovalLoading} categories={categories} classifications={classifications} subclassifications={subclassifications} />;
  }
  if (showCategoryScreen) {
    return <CategoryClassificationScreen onBack={() => {
      setShowCategoryScreen(false);
      fetchCategories();
      fetchProducts();
      fetchClassifications();
      fetchSubclassifications();
    }} />;
  }
  return <div className="dashboard-content width-100">
      <LoadingModal show={loading || editProductLoading} message={editProductLoading ? "Loading product details..." : "Loading Item Master..."} />
      <div className="item-master-page">
        {/* Header Section */}
        <div className="item-master-header">
          <div className="item-master-header-left">
            <h1 className="item-master-title">Item Master</h1>
            <div className="item-master-dropdown" style={{
            position: 'relative'
          }} ref={itemTypesDropdownRef}>
              <button className="item-master-dropdown-btn" onClick={() => {
              setShowItemTypesDropdown(true);
            }}>
                {itemType || "ITEM TYPES"}
                <ChevronDown size={16} />
              </button>
              {showItemTypesDropdown && <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '4px',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              zIndex: 1000,
              minWidth: '200px',
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
                    <button style={{
                display: 'block',
                width: '100%',
                padding: '8px 16px',
                textAlign: 'left',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }} onClick={() => {
                setItemType("");
                setShowItemTypesDropdown(false);
              }} onMouseEnter={e => e.target.style.backgroundColor = '#f9fafb'} onMouseLeave={e => e.target.style.backgroundColor = 'transparent'}>
                      ITEM TYPES
                    </button>
                    {itemTypes.map(category => <button key={category.value} style={{
                display: 'block',
                width: '100%',
                padding: '8px 16px',
                textAlign: 'left',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '0.875rem',
                borderTop: '1px solid #e5e7eb'
              }} onClick={() => {
                setItemType(category.value);
                setShowItemTypesDropdown(false);
              }} onMouseEnter={e => e.target.style.backgroundColor = '#f9fafb'} onMouseLeave={e => e.target.style.backgroundColor = 'transparent'}>
                        {category.label}
                      </button>)}
                  </div>}
            </div>
            <button className="item-master-markup-btn" onClick={() => setIsMarkupModalOpen(true)}>
              <BarChart3 size={18} />
              Markup Setting
            </button>
            <button className="item-master-edit-btn" onClick={() => setShowCategoryScreen(true)}>
              <Edit size={18} />
              Edit Categories
            </button>
            <button className="item-master-edit-btn" onClick={() => setIsUomManagerOpen(true)}>
              <Edit size={18} />
              Unit of Measure
            </button>
          </div>
          <div className="item-master-header-right">
            <button className="item-master-add-btn" onClick={() => setIsAddProductModalOpen(true)} style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 18px",
            borderRadius: 8,
            border: "1px solid #002347",
            backgroundColor: "#ffffff",
            color: "#002347",
            fontSize: "0.875rem",
            fontWeight: 500
          }}>
              <span style={{
              fontSize: 18,
              lineHeight: 1
            }}>+</span>
              Add Product
            </button>
            <button className="item-master-import-btn" onClick={() => setIsImportModalOpen(true)}>
              <Download size={18} />
              Import CSV
            </button>
            <button className="item-master-template-btn" onClick={handleDownloadTemplate}>
              <Download size={18} />
              Download Template
            </button>
            <button className="item-master-approval-btn" onClick={() => setShowItemApproval(true)}>
              Item Approval
            </button>
          </div>
        </div>

        {/* Item Master Table Section */}
        <div className="item-master-table-section">
          <div className="item-master-search-filter">
            <div className="item-master-search-container">
              <Search className="item-master-search-icon" size={18} />
              <input type="text" className="item-master-search-input" placeholder="Search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
            </div>
            <div className="item-master-filter-right">
              <div style={{
              position: 'relative'
            }}>
                <button className="item-master-filter-btn" onClick={() => {
                setShowCategoryDropdown(!showCategoryDropdown);
              }}>
                  {categoryFilter}
                  <ChevronDown size={16} />
                </button>
                {showCategoryDropdown && <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '4px',
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                zIndex: 1000,
                minWidth: '200px',
                maxHeight: '300px',
                overflowY: 'auto'
              }}>
                    <button style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 16px',
                  textAlign: 'left',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  fontSize: '0.875rem'
                }} onClick={() => {
                  setCategoryFilter("Category");
                  setShowCategoryDropdown(false);
                }} onMouseEnter={e => e.target.style.backgroundColor = '#f9fafb'} onMouseLeave={e => e.target.style.backgroundColor = 'transparent'}>
                      Category
                    </button>
                    {categories.map(category => <button key={category.id} style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 16px',
                  textAlign: 'left',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  borderTop: '1px solid #e5e7eb'
                }} onClick={() => {
                  setCategoryFilter(category.name);
                  setShowCategoryDropdown(false);
                }} onMouseEnter={e => e.target.style.backgroundColor = '#f9fafb'} onMouseLeave={e => e.target.style.backgroundColor = 'transparent'}>
                        {category.name}
                      </button>)}
                  </div>}
              </div>
              <button className="item-master-export-btn" onClick={() => exportToCSV(filteredItemMasterData)}>
                <Upload size={18} />
                Export
              </button>
            </div>
          </div>
          <div className="item-master-table-container">
            <DataTable columns={itemMasterColumns} data={filteredItemMasterData} customStyles={customStyles} pagination paginationPerPage={10} paginationRowsPerPageOptions={[]} paginationComponent={CustomPagination} paginationComponentOptions={{
            rowsPerPageText: '',
            rangeSeparatorText: 'of',
            noRowsPerPage: true,
            selectAllRowsItem: false
          }} highlightOnHover fixedHeader fixedHeaderScrollHeight="600px" noDataComponent={<div style={{
            padding: "40px 20px",
            textAlign: "center",
            color: "#666",
            fontSize: "1rem"
          }}>
                  No items found
                </div>} />
          </div>
        </div>

        {/* Activity Log Section */}
        <div className="item-master-activity-section">
          <h2 className="item-master-activity-title">Activity Log</h2>
          <div className="item-master-activity-search-filter">
            <div className="item-master-search-container">
              <Search className="item-master-search-icon" size={18} />
              <input type="text" className="item-master-search-input" placeholder="Search" value={activitySearchQuery} onChange={e => setActivitySearchQuery(e.target.value)} />
            </div>
            <div className="item-master-activity-filter-right" style={{
            position: "relative"
          }} ref={activityDropdownRef}>
              <button className="item-master-filter-btn" onClick={() => setActivityDropdownOpen(v => !v)} style={{
              position: "relative"
            }}>
                {activityFilter}
                <ChevronDown size={16} />
              </button>
              {activityDropdownOpen && <div style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              right: 0,
              zIndex: 100,
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
              minWidth: "160px",
              overflow: "hidden"
            }}>
                  {["All Activities", "CREATE", "UPDATE", "DELETE", "VIEW"].map(opt => <div key={opt} onClick={() => {
                setActivityFilter(opt);
                setActivityDropdownOpen(false);
              }} style={{
                padding: "10px 16px",
                cursor: "pointer",
                fontSize: "0.875rem",
                color: activityFilter === opt ? "#0ea5e9" : "#374151",
                fontWeight: activityFilter === opt ? 600 : 400,
                backgroundColor: activityFilter === opt ? "#f0f9ff" : "transparent"
              }} onMouseEnter={e => e.currentTarget.style.backgroundColor = activityFilter === opt ? "#f0f9ff" : "#f9fafb"} onMouseLeave={e => e.currentTarget.style.backgroundColor = activityFilter === opt ? "#f0f9ff" : "transparent"}>
                      {opt}
                    </div>)}
                </div>}
            </div>
          </div>
          <div className="item-master-activity-table-container">
            <DataTable columns={activityLogColumns} data={activityLogData} customStyles={customStyles} striped highlightOnHover noHeader={false} pagination paginationServer paginationTotalRows={activityTotalRecords} paginationPerPage={ACTIVITY_LIMIT} paginationDefaultPage={activityPage} onChangePage={newPage => setActivityPage(newPage)} paginationComponent={CustomPagination} paginationRowsPerPageOptions={[]} paginationComponentOptions={{
            rowsPerPageText: '',
            rangeSeparatorText: 'of',
            noRowsPerPage: true,
            selectAllRowsItem: false
          }} progressPending={activityLoading} noDataComponent={<div style={{
            padding: "16px",
            textAlign: "center",
            fontSize: "0.875rem",
            color: "#6b7280"
          }}>
                  No activity records
                </div>} />
          </div>
        </div>

        {/* Import Products Modal */}
        <ImportProductsModal isOpen={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} onImport={handleImport} />

        {/* Add Product Modal */}
        {isAddProductModalOpen && <div className="jmodal-overlay" onClick={e => {
        if (e.target === e.currentTarget) {
          setIsAddProductModalOpen(false);
        }
      }}>
            <div className="jmodal-content" style={{
          minWidth: 400,
          maxWidth: 620,
          width: "60%",
          maxHeight: "85vh",
          overflowY: "auto",
          textAlign: "left",
          borderRadius: 24,
          padding: 24
        }}>
              <h2 style={{
            marginTop: 0,
            marginBottom: 16,
            textAlign: "center",
            fontSize: "1.5rem",
            fontWeight: 600,
            color: "#111827"
          }}>
                Add Product
              </h2>

              <div style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
            marginBottom: 16
          }}>
                {/* SKU Code */}
                <div>
                  <label style={{
                display: "block",
                fontSize: 12,
                color: "#6b7280",
                marginBottom: 4
              }}>
                    SKU Code
                  </label>
                  <input type="text" value={addProductForm.sku} onChange={e => handleAddProductChange("sku", e.target.value)} style={{
                width: "100%",
                height: 40,
                borderRadius: 8,
                border: "1px solid #d1d5db",
                padding: "0 12px",
                fontSize: 14
              }} />
                </div>

                {/* Bar Code */}
                <div>
                  <label style={{
                display: "block",
                fontSize: 12,
                color: "#6b7280",
                marginBottom: 4
              }}>
                    Bar Code
                  </label>
                  <input type="text" value={addProductForm.barcode} onChange={e => handleAddProductChange("barcode", e.target.value)} style={{
                width: "100%",
                height: 40,
                borderRadius: 8,
                border: "1px solid #d1d5db",
                padding: "0 12px",
                fontSize: 14
              }} />
                </div>

                {/* Product Name */}
                <div>
                  <label style={{
                display: "block",
                fontSize: 12,
                color: "#6b7280",
                marginBottom: 4
              }}>
                    Product Name
                  </label>
                  <input type="text" value={addProductForm.name} onChange={e => handleAddProductChange("name", e.target.value)} style={{
                width: "100%",
                height: 40,
                borderRadius: 8,
                border: "1px solid #d1d5db",
                padding: "0 12px",
                fontSize: 14
              }} />
                </div>

                {/* Item Description (details) */}
                <div>
                  <label style={{
                display: "block",
                fontSize: 12,
                color: "#6b7280",
                marginBottom: 4
              }}>
                    Item Description
                  </label>
                  <input type="text" value={addProductForm.details} onChange={e => handleAddProductChange("details", e.target.value)} style={{
                width: "100%",
                height: 40,
                borderRadius: 8,
                border: "1px solid #d1d5db",
                padding: "0 12px",
                fontSize: 14
              }} />
                </div>

                {/* Status */}
                <div>
                  <label style={{
                display: "block",
                fontSize: 12,
                color: "#6b7280",
                marginBottom: 4
              }}>
                    Status
                  </label>
                  <select value={addProductForm.status} onChange={e => handleAddProductChange("status", e.target.value)} style={{
                width: "100%",
                height: 40,
                borderRadius: 8,
                border: "1px solid #d1d5db",
                padding: "0 12px",
                fontSize: 14,
                backgroundColor: "#fff"
              }}>
                    <option value="">Select Status</option>
                    <option value="NEW">New</option>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>

                {/* Max Order QTY */}
                <div>
                  <label style={{
                display: "block",
                fontSize: 12,
                color: "#6b7280",
                marginBottom: 4
              }}>
                    Max. Order QTY (Regulated Items)
                  </label>
                  <input type="number" value={addProductForm.regulatedLimit} onChange={e => handleAddProductChange("regulatedLimit", e.target.value)} style={{
                width: "100%",
                height: 40,
                borderRadius: 8,
                border: "1px solid #d1d5db",
                padding: "0 12px",
                fontSize: 14
              }} />
                </div>

                {/* Category */}
                <div>
                  <label style={{
                display: "block",
                fontSize: 12,
                color: "#6b7280",
                marginBottom: 4
              }}>
                    Category
                  </label>
                  <select value={addProductForm.productCategoryId} onChange={e => handleAddProductChange("productCategoryId", e.target.value ? Number(e.target.value) : "")} style={{
                width: "100%",
                height: 40,
                borderRadius: 8,
                border: "1px solid #d1d5db",
                padding: "0 12px",
                fontSize: 14,
                backgroundColor: "#fff"
              }}>
                    <option value="">Select Category</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>

                {/* Classification */}
                <div>
                  <label style={{
                display: "block",
                fontSize: 12,
                color: "#6b7280",
                marginBottom: 4
              }}>
                    Classification
                  </label>
                  <select value={addProductForm.productClassificationId} onChange={e => handleAddProductChange("productClassificationId", e.target.value ? Number(e.target.value) : "")} style={{
                width: "100%",
                height: 40,
                borderRadius: 8,
                border: "1px solid #d1d5db",
                padding: "0 12px",
                fontSize: 14,
                backgroundColor: "#fff"
              }}>
                    <option value="">Select Classification</option>
                    {classifications.filter(c => !addProductForm.productCategoryId || (c.product_category_id ?? c.productCategoryId) === Number(addProductForm.productCategoryId)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>

                {/* Sub-Classification */}
                <div>
                  <label style={{
                display: "block",
                fontSize: 12,
                color: "#6b7280",
                marginBottom: 4
              }}>
                    Sub-Classification
                  </label>
                  <select value={addProductForm.subclassficationId} onChange={e => handleAddProductChange("subclassficationId", e.target.value ? Number(e.target.value) : "")} style={{
                width: "100%",
                height: 40,
                borderRadius: 8,
                border: "1px solid #d1d5db",
                padding: "0 12px",
                fontSize: 14,
                backgroundColor: "#fff"
              }}>
                    <option value="">Select Sub-Classification</option>
                    {subclassifications.filter(sc => !addProductForm.productClassificationId || (sc.parent_id ?? sc.parentId) === Number(addProductForm.productClassificationId)).map(sc => <option key={sc.id} value={sc.id}>{sc.name}</option>)}
                  </select>
                </div>

                {/* Brand */}
                <div>
                  <label style={{
                display: "block",
                fontSize: 12,
                color: "#6b7280",
                marginBottom: 4
              }}>
                    Brand
                  </label>
                  <input type="text" value={addProductForm.brand} onChange={e => handleAddProductChange("brand", e.target.value)} style={{
                width: "100%",
                height: 40,
                borderRadius: 8,
                border: "1px solid #d1d5db",
                padding: "0 12px",
                fontSize: 14
              }} />
                </div>

                {/* Variant */}
                <div>
                  <label style={{
                display: "block",
                fontSize: 12,
                color: "#6b7280",
                marginBottom: 4
              }}>
                    Variant
                  </label>
                  <input type="text" value={addProductForm.variant} onChange={e => handleAddProductChange("variant", e.target.value)} style={{
                width: "100%",
                height: 40,
                borderRadius: 8,
                border: "1px solid #d1d5db",
                padding: "0 12px",
                fontSize: 14
              }} />
                </div>

                {/* BuCor Remarks */}
                <div>
                  <label style={{
                display: "block",
                fontSize: 12,
                color: "#6b7280",
                marginBottom: 4
              }}>
                    BuCor Remarks
                  </label>
                  <input type="text" value={addProductForm.bucorRemarks} onChange={e => handleAddProductChange("bucorRemarks", e.target.value)} style={{
                width: "100%",
                height: 40,
                borderRadius: 8,
                border: "1px solid #d1d5db",
                padding: "0 12px",
                fontSize: 14
              }} />
                </div>
              </div>

              <div style={{
            display: "flex",
            gap: 12,
            marginTop: 8
          }}>
                <button type="button" className="modalButton2" style={{
              flex: 1,
              backgroundColor: "#ffffff",
              color: "#000000",
              border: "1.5px solid #000000",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }} onClick={() => setIsAddProductModalOpen(false)} disabled={addProductLoading}>
                  Cancel
                </button>
                <button type="button" className="modalButton1" style={{
              flex: 1,
              backgroundColor: "#002347",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }} onClick={handleAddProductSubmit} disabled={addProductLoading}>
                  {addProductLoading ? "Adding..." : "Add"}
                </button>
              </div>
            </div>
          </div>}

        {/* Markup Setting Modal */}
        <MarkupSettingModal isOpen={isMarkupModalOpen} onClose={() => setIsMarkupModalOpen(false)} onSuccess={fetchProducts} />

        {/* Unit of Measure Manager Modal */}
        <UomManager isOpen={isUomManagerOpen} onClose={() => setIsUomManagerOpen(false)} onSuccess={fetchProducts} />

        {/* Edit Product Modal */}
        <EditProductModal product={editProduct} isOpen={isEditProductModalOpen} onClose={() => {
        setIsEditProductModalOpen(false);
        setEditProduct(null);
      }} onSave={handleSaveProduct} categories={categories} classifications={classifications} subclassifications={subclassifications} />

        {/* Product History Modal */}
        <Modal isOpen={isHistoryModalOpen} onRequestClose={() => {
        setIsHistoryModalOpen(false);
        setHistoryProduct(null);
      }} ariaHideApp={false} style={{
        overlay: {
          backgroundColor: "rgba(0,0,0,0.45)",
          zIndex: 1000
        },
        content: {
          maxWidth: 980,
          width: "92%",
          inset: "50% auto auto 50%",
          transform: "translate(-50%, -50%)",
          borderRadius: 18,
          border: "1px solid #e5e7eb",
          padding: 0,
          overflow: "hidden"
        }
      }}>
          <div style={{
          backgroundColor: "#ffffff"
        }}>
            <div style={{
            padding: "20px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}>
              <div style={{
              flex: 1
            }} />
              <div style={{
              flex: 2,
              textAlign: "center"
            }}>
                <div style={{
                fontSize: 22,
                fontWeight: 600,
                color: "#111827"
              }}>History</div>
              </div>
              <div style={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end"
            }}>
                <button type="button" onClick={() => {
                setIsHistoryModalOpen(false);
                setHistoryProduct(null);
              }} style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                border: "1px solid #ffffff",
                backgroundColor: "#ffffff",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center"
              }} aria-label="Close history modal">
                  <X size={18} />
                </button>
              </div>
            </div>

            <div style={{
            padding: "20px 24px"
          }}>
              <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              marginBottom: 16
            }}>
                <div style={{
                display: "flex",
                gap: 12,
                alignItems: "center"
              }}>
                  <div>
                    <div style={{
                    fontSize: 12,
                    color: "#6b7280",
                    marginBottom: 6
                  }}>From</div>
                    <input type="date" value={historyFrom} onChange={e => {
                    const v = e.target.value;
                    setHistoryFrom(v);
                    setHistoryPage(1);
                    fetchProductHistory({
                      productId: historyProduct?.id,
                      dateFrom: v,
                      dateTo: historyTo,
                      page: 1,
                      limit: HISTORY_LIMIT
                    });
                  }} style={{
                    height: 40,
                    borderRadius: 10,
                    border: "1px solid #e5e7eb",
                    padding: "0 12px",
                    fontSize: 14,
                    minWidth: 160
                  }} />
                  </div>
                  <div>
                    <div style={{
                    fontSize: 12,
                    color: "#6b7280",
                    marginBottom: 6
                  }}>To</div>
                    <input type="date" value={historyTo} onChange={e => {
                    const v = e.target.value;
                    setHistoryTo(v);
                    setHistoryPage(1);
                    fetchProductHistory({
                      productId: historyProduct?.id,
                      dateFrom: historyFrom,
                      dateTo: v,
                      page: 1,
                      limit: HISTORY_LIMIT
                    });
                  }} style={{
                    height: 40,
                    borderRadius: 10,
                    border: "1px solid #e5e7eb",
                    padding: "0 12px",
                    fontSize: 14,
                    minWidth: 160
                  }} />
                  </div>
                </div>

                <button type="button" onClick={() => {
                setHistoryFrom("");
                setHistoryTo("");
                setHistoryPage(1);
                fetchProductHistory({
                  productId: historyProduct?.id,
                  page: 1,
                  limit: HISTORY_LIMIT
                });
              }} style={{
                height: 40,
                padding: "0 14px",
                borderRadius: 10,
                border: "1px solid #e5e7eb",
                backgroundColor: "#ffffff",
                cursor: "pointer",
                fontSize: 14,
                color: "#111827"
              }}>
                  Filters
                </button>
              </div>

              <div style={{
              borderRadius: 16,
              overflow: "hidden",
              border: "1px solid #e5e7eb"
            }}>
                <div style={{
                backgroundColor: "#0b2a43",
                color: "#ffffff"
              }}>
                  <div style={{
                  display: "grid",
                  gridTemplateColumns: "120px 1fr 1fr 220px",
                  gap: 0
                }}>
                    {["Table: #", "Activity", "Changed by", "Date"].map(h => <div key={h} style={{
                    padding: "14px 16px",
                    fontSize: 13,
                    fontWeight: 600
                  }}>
                        {h}
                      </div>)}
                  </div>
                </div>

                <div style={{
                maxHeight: 360,
                overflowY: "auto",
                backgroundColor: "#ffffff"
              }}>
                  {historyLoading ? <div style={{
                  padding: 20,
                  textAlign: "center",
                  color: "#6b7280",
                  fontSize: 14
                }}>
                      Loading history...
                    </div> : historyRows.length === 0 ? <div style={{
                  padding: 20,
                  textAlign: "center",
                  color: "#6b7280",
                  fontSize: 14
                }}>
                      No history found.
                    </div> : historyRows.map(r => <div key={r.id} style={{
                  display: "grid",
                  gridTemplateColumns: "120px 1fr 1fr 220px",
                  borderTop: "1px solid #f3f4f6"
                }}>
                        <div style={{
                    padding: "14px 16px",
                    fontSize: 14,
                    color: "#111827"
                  }}>{r.tableNo}</div>
                        <div style={{
                    padding: "14px 16px",
                    fontSize: 14,
                    color: "#111827"
                  }}>{r.table || "-"}</div>
                        <div style={{
                    padding: "14px 16px",
                    fontSize: 14,
                    color: "#111827"
                  }}>{r.changedBy || "-"}</div>
                        <div style={{
                    padding: "14px 16px",
                    fontSize: 14,
                    color: "#111827"
                  }}>{r.date || "-"}</div>
                      </div>)}
                </div>
              </div>
              <div style={{
              borderTop: "1px solid #e5e7eb",
              backgroundColor: "#f9fafb"
            }}>
                <CustomPagination rowCount={historyTotalRecords} rowsPerPage={HISTORY_LIMIT} currentPage={historyPage} onChangePage={newPage => {
                setHistoryPage(newPage);
                fetchProductHistory({
                  productId: historyProduct?.id,
                  dateFrom: historyFrom || undefined,
                  dateTo: historyTo || undefined,
                  page: newPage,
                  limit: HISTORY_LIMIT
                });
              }} />
              </div>
            </div>
          </div>
        </Modal>

        {/* Duplicate Products Modal */}
        <Modal isOpen={showDuplicateModal} onRequestClose={() => setShowDuplicateModal(false)} className="import-products-modal" overlayClassName="import-products-modal-overlay" ariaHideApp={false}>
          <div className="import-products-modal-content">
            <button className="import-products-close-btn-top" onClick={() => setShowDuplicateModal(false)}>
              <X size={20} />
            </button>
            
            <div className="import-products-body">
              <h3 className="import-products-card-title">Duplicate Products Found</h3>
              
              <div className="import-products-form-card">
                <p className="import-products-confirm-text" style={{
                marginBottom: '16px'
              }}>
                  {duplicateProducts.length > 0 ? `Found ${duplicateProducts.length} duplicate product(s). Please review the list below.` : 'Duplicate products were found in your file.'}
                </p>

                <div className="duplicate-products-list-container" style={{
                maxHeight: '400px',
                overflowY: 'auto',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '20px'
              }}>
                  <table style={{
                  width: '100%',
                  borderCollapse: 'collapse'
                }}>
                    <thead>
                      <tr style={{
                      borderBottom: '2px solid #e5e7eb'
                    }}>
                        <th style={{
                        padding: '8px',
                        textAlign: 'left',
                        fontSize: '0.875rem',
                        fontWeight: 600
                      }}>SKU</th>
                        <th style={{
                        padding: '8px',
                        textAlign: 'left',
                        fontSize: '0.875rem',
                        fontWeight: 600
                      }}>Barcode</th>
                        <th style={{
                        padding: '8px',
                        textAlign: 'left',
                        fontSize: '0.875rem',
                        fontWeight: 600
                      }}>Name</th>
                        <th style={{
                        padding: '8px',
                        textAlign: 'left',
                        fontSize: '0.875rem',
                        fontWeight: 600
                      }}>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {duplicateProducts.map((product, index) => <tr key={index} style={{
                      borderBottom: '1px solid #f3f4f6'
                    }}>
                          <td style={{
                        padding: '8px',
                        fontSize: '0.875rem'
                      }}>{product.sku}</td>
                          <td style={{
                        padding: '8px',
                        fontSize: '0.875rem'
                      }}>{product.barcode}</td>
                          <td style={{
                        padding: '8px',
                        fontSize: '0.875rem'
                      }}>{product.name}</td>
                          <td style={{
                        padding: '8px',
                        fontSize: '0.875rem',
                        color: '#6b7280'
                      }}>{product.source}</td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>

                <div className="import-products-confirm-messages">
                  <p className="import-products-confirm-text">
                    Do you want to proceed with the import? This will update existing products with matching SKUs.
                  </p>
                </div>

                {/* Buttons */}
                <div className="import-products-confirm-actions">
                  <button className="import-products-proceed-btn" onClick={handleProceedWithDuplicates}>
                    Proceed Anyway
                  </button>
                  <button className="import-products-cancel-confirm-btn" onClick={() => setShowDuplicateModal(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </div>;
};
export default ItemMaster;